import { instrumentedFetch } from '@syia-ai/observability';

/**
 * The shell's single browser-side HTTP funnel.
 *
 * A pass-through by design — it adds no headers, no retries, no error mapping,
 * and deliberately keeps `fetch`'s exact semantics (returns the Response for any
 * status, rethrows only on transport failure) so adopting it at a call site is
 * never a behaviour change.
 *
 * Its whole value is being ONE place. `instrumentedFetch` reads the
 * `x-request-id` the Edge middleware minted back off the response, which is what
 * lets a browser failure be traced to the exact server line rather than only to
 * a session; and it emits `status: 0` for a request that never reached a server
 * at all — DNS failure, CORS preflight rejection, offline, blocked by an
 * extension. That last class is structurally invisible to backend logs, and on
 * the shell it is the most likely failure of all: it fronts every App, so "the
 * request never left the browser" is a real and otherwise-silent outcome.
 *
 * Instrument the funnel, not the call sites: a fetch added later that imports
 * this is covered automatically, and one that calls global `fetch` directly is
 * greppable. Editing N call sites gives neither property.
 *
 * Server-side fetches (pages/api/*) do NOT belong here — those are server→server
 * hops with no browser to correlate from, and their route already emits an
 * access line via withApiLogging.
 */
export const httpFetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> =>
  instrumentedFetch(input, init);
